import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ChatWidget from './ChatWidget';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { ScrollArea } from './ui/scroll-area';
import {
  LayoutDashboard,
  Users,
  Calendar,
  Receipt,
  MessageSquare,
  FileText,
  Leaf,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  Bell,
  Moon,
  Sun,
  Crown,
  FileSpreadsheet,
  User,
  Clock,
  FolderOpen,
  Check,
  CheckCheck,
  Trash2,
  Loader2,
  Send,
  Bot,
  Sparkles,
  Minus,
  GraduationCap,
  BarChart3,
  Shield,
  CalendarDays,
  ClipboardCheck,
  Briefcase,
  HardHat
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Tableau de bord', icon: LayoutDashboard, module: 'dashboard' },
  { path: '/employees', label: 'Employés', icon: Users, module: 'employees' },
  { path: '/habilitations', label: 'Habilitations', icon: FileText, module: 'habilitations' },
  { path: '/absences', label: 'Congés', icon: Calendar, module: 'conges' },
  { path: '/planning', label: 'Planning', icon: CalendarDays, module: 'planning' },
  { path: '/payroll', label: 'Préparation Paie', icon: Receipt, module: 'payroll' },
  { path: '/emargement', label: 'Émargement', icon: ClipboardCheck, module: 'emargement' },
  { path: '/expenses', label: 'Notes de frais', icon: Receipt, module: 'expenses' },
  { path: '/interviews', label: 'Entretiens', icon: FileText, module: 'entretiens' },
  { path: '/documents', label: 'Documents', icon: FolderOpen, module: 'documents' },
  { path: '/formations', label: 'Formations', icon: GraduationCap, module: 'formations' },
  { path: '/sous-traitants', label: 'Sous-traitants', icon: HardHat, module: 'sous_traitants' },
  { path: '/rse', label: 'RSE', icon: Leaf, module: 'rse' },
  { path: '/reporting', label: 'Reporting', icon: BarChart3, roles: ['admin', 'rh', 'comptabilite'], module: 'reporting' },
  {
    label: 'Administration',
    icon: Settings,
    roles: ['admin', 'rh'],
    module: 'admin',
    children: [
      { path: '/admin', label: 'Paramètres', icon: Settings, module: 'admin' },
      { path: '/audit-log', label: 'Journal d\'audit', icon: Shield, module: 'admin' },
    ]
  },
];

const employeeItems = [
  { path: '/portal', label: 'Mon Espace', icon: User },
];

const superAdminItems = [
  { path: '/superadmin', label: 'Super Admin', icon: Crown },
  { path: '/prospects', label: 'Prospects CRM', icon: Briefcase },
];

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasModuleAccess, allModulesAccess } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifCount, setNotifCount] = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  // Floating chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);
  const chatInputRef = useRef(null);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [user]);

  // Fetch notification count
  const fetchNotifCount = useCallback(async () => {
    if (!user) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotifCount(data.count);
      }
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  }, [user]);

  // Mark notification as read
  const markAsRead = async (notifId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notifId}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n =>
        n.id === notifId ? { ...n, lu: true } : n
      ));
      setNotifCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/read-all`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, lu: true })));
      setNotifCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Delete notification
  const deleteNotification = async (notifId, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${notifId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const notif = notifications.find(n => n.id === notifId);
      setNotifications(prev => prev.filter(n => n.id !== notifId));
      if (notif && !notif.lu) {
        setNotifCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Initial fetch and polling
  useEffect(() => {
    fetchNotifCount();
    const interval = setInterval(fetchNotifCount, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, [fetchNotifCount]);

  // Fetch full list when dropdown opens
  useEffect(() => {
    if (notifOpen) {
      setNotifLoading(true);
      fetchNotifications().finally(() => setNotifLoading(false));
    }
  }, [notifOpen, fetchNotifications]);

  // Format notification date
  const formatNotifDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return 'À l\'instant';
    if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `Il y a ${Math.floor(diff / 86400)}j`;
    return date.toLocaleDateString('fr-FR');
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const [openMenus, setOpenMenus] = useState({});

  const toggleMenu = (label) => {
    setOpenMenus(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const hasAccess = (item) => {
    // Check role-based access
    if (item.roles && !item.roles.includes(user?.role)) {
      return false;
    }

    // Check module-based access (hasModuleAccess handles super_admin and allModulesAccess internally)
    if (item.module) {
      if (!hasModuleAccess(item.module)) {
        return false;
      }
    }

    return true;
  };

  const NavLink = ({ item, mobile = false }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    
    // Check access permission
    if (!hasAccess(item)) return null;

    // If item has children, render collapsible menu
    if (item.children) {
      const isOpen = openMenus[item.label];
      const hasActiveChild = item.children.some(child => location.pathname === child.path);
      
      return (
        <div className="space-y-1">
          <button
            onClick={() => toggleMenu(item.label)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
              hasActiveChild
                ? 'bg-[#0F5156]/10 text-[#0F5156] dark:bg-[#0F5156]/20 dark:text-[#69AFAF] font-medium'
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
          >
            <Icon className="w-5 h-5" strokeWidth={1.5} />
            <span>{item.label}</span>
            {isOpen ? (
              <ChevronDown className="w-4 h-4 ml-auto" />
            ) : (
              <ChevronRight className="w-4 h-4 ml-auto" />
            )}
          </button>
          {isOpen && (
            <div className="ml-4 pl-4 border-l-2 border-slate-200 dark:border-slate-700 space-y-1">
              {item.children.map(child => {
                const ChildIcon = child.icon;
                const isChildActive = location.pathname === child.path;
                return (
                  <Link
                    key={child.path}
                    to={child.path}
                    onClick={() => mobile && setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm ${
                      isChildActive
                        ? 'bg-[#0F5156]/10 text-[#0F5156] dark:bg-[#0F5156]/20 dark:text-[#69AFAF] font-medium'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    <ChildIcon className="w-4 h-4" strokeWidth={1.5} />
                    <span>{child.label}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    
    return (
      <Link
        to={item.path}
        onClick={() => mobile && setSidebarOpen(false)}
        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
          isActive 
            ? 'bg-[#0F5156]/10 text-[#0F5156] dark:bg-[#0F5156]/20 dark:text-[#69AFAF] font-medium' 
            : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
        }`}
        data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
      >
        <Icon className="w-5 h-5" strokeWidth={1.5} />
        <span>{item.label}</span>
        {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
      </Link>
    );
  };

  // Chat functions
  const scrollChatToBottom = () => {
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  useEffect(() => { scrollChatToBottom(); }, [chatMessages]);
  useEffect(() => { if (chatOpen && chatInputRef.current) chatInputRef.current.focus(); }, [chatOpen]);

  // Load chat history on first open
  const chatHistoryLoaded = useRef(false);
  const loadChatHistory = useCallback(async () => {
    if (chatHistoryLoaded.current) return;
    chatHistoryLoaded.current = true;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const history = data.reverse().flatMap(item => [
          { role: 'user', content: item.message },
          { role: 'assistant', content: item.response }
        ]);
        setChatMessages(history);
      }
    } catch (e) { console.error('Chat history error:', e); }
  }, []);

  useEffect(() => { if (chatOpen) loadChatHistory(); }, [chatOpen, loadChatHistory]);

  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || chatLoading) return;
    const msg = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: msg }]);
    setChatLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: msg })
      });
      const data = await res.json();
      setChatMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (e) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Desole, une erreur s\'est produite. Veuillez reessayer.' }]);
    } finally { setChatLoading(false); }
  };

  const SidebarContent = ({ mobile = false }) => {
    const { getTrialInfo } = useAuth();
    const trialInfo = getTrialInfo();
    
    return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center gap-3 px-6 py-6 border-b border-slate-200 dark:border-slate-700">
        <div className="w-10 h-10 rounded-xl bg-[#0F5156] flex items-center justify-center">
          <Users className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="font-bold text-lg text-slate-900 dark:text-white">Nexus SIRH</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400">Gestion RH</p>
        </div>
      </div>

      {/* Trial Banner */}
      {trialInfo && (
        <div className="mx-4 mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
            <Clock className="w-4 h-4" />
            <span className="text-sm font-medium">Période d'essai</span>
          </div>
          <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">
            {trialInfo.daysRemaining} jour(s) restant(s)
          </p>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
        {/* Employee Portal - shown for all users */}
        {employeeItems.map((item) => (
          <NavLink key={item.path} item={item} mobile={mobile} />
        ))}
        
        <div className="my-2 border-t border-slate-200 dark:border-slate-700"></div>
        
        {navItems
          .filter(item => hasAccess(item))
          .map((item) => (
            <NavLink key={item.path || item.label} item={item} mobile={mobile} />
          ))}
        
        {/* Super Admin Section */}
        {user && user.role === 'super_admin' && (
          <>
            <div className="my-4 border-t border-slate-200 dark:border-slate-700"></div>
            {superAdminItems.map((item) => (
              <NavLink key={item.path} item={item} mobile={mobile} />
            ))}
          </>
        )}
      </nav>

      {/* User */}
      <div className="px-4 py-4 border-t border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-3 px-2">
          <Avatar className="w-9 h-9">
            <AvatarFallback className="bg-[#C27868] text-white text-sm">
              {user?.prenom?.[0]}{user?.nom?.[0]}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
              {user?.prenom} {user?.nom}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 capitalize">
              {user?.role}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="w-72 p-0">
          <SidebarContent mobile />
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className="md:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between px-4 md:px-8 h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </Button>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white hidden sm:block">
                {navItems.find(item => item.path === location.pathname)?.label || 'Tableau de bord'}
              </h2>
            </div>

            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={toggleDarkMode}
                data-testid="theme-toggle-btn"
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              {/* Notifications Dropdown */}
              <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                    <Bell className="w-5 h-5" />
                    {notifCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-xs font-medium rounded-full px-1">
                        {notifCount > 99 ? '99+' : notifCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 p-0">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
                    <h3 className="font-semibold text-slate-900 dark:text-white">Notifications</h3>
                    {notifCount > 0 && (
                      <button
                        onClick={markAllAsRead}
                        className="text-xs text-[#0F5156] hover:underline flex items-center gap-1"
                      >
                        <CheckCheck className="w-3 h-3" />
                        Tout marquer comme lu
                      </button>
                    )}
                  </div>
                  <ScrollArea className="max-h-[400px]">
                    {notifLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                      </div>
                    ) : notifications.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 text-slate-500">
                        <Bell className="w-8 h-8 mb-2 opacity-50" />
                        <p className="text-sm">Aucune notification</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-100 dark:divide-slate-800">
                        {notifications.map((notif) => (
                          <div
                            key={notif.id}
                            className={`px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors group ${
                              !notif.lu ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                            }`}
                            onClick={() => {
                              if (!notif.lu) markAsRead(notif.id);
                              if (notif.lien) {
                                navigate(notif.lien);
                                setNotifOpen(false);
                              }
                            }}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                                !notif.lu ? 'bg-[#0F5156]' : 'bg-transparent'
                              }`} />
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm ${!notif.lu ? 'font-semibold' : 'font-medium'} text-slate-900 dark:text-white`}>
                                  {notif.titre}
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">
                                  {notif.message}
                                </p>
                                <p className="text-xs text-slate-400 mt-1">
                                  {formatNotifDate(notif.created_at)}
                                </p>
                              </div>
                              <button
                                onClick={(e) => deleteNotification(notif.id, e)}
                                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-all"
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4 text-slate-400" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                  {notifications.length > 0 && (
                    <div className="border-t border-slate-200 dark:border-slate-700 p-2">
                      <Button
                        variant="ghost"
                        className="w-full text-sm text-[#0F5156] hover:text-[#0d464a]"
                        onClick={() => {
                          navigate('/settings?tab=notifications');
                          setNotifOpen(false);
                        }}
                      >
                        Voir toutes les notifications
                      </Button>
                    </div>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2" data-testid="user-menu-btn">
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="bg-[#C27868] text-white text-xs">
                        {user?.prenom?.[0]}{user?.nom?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline text-sm font-medium">
                      {user?.prenom}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.prenom} {user?.nom}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
                    <Settings className="w-4 h-4 mr-2" />
                    Paramètres
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-menu-item">
                    <LogOut className="w-4 h-4 mr-2" />
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 md:p-8 pb-24 md:pb-8">
          {children}
        </main>
      </div>

      {/* Floating Chat Widget */}
      {user && (
        <>
          {/* Chat Panel */}
          {chatOpen && (
            <div className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[500px] max-h-[calc(100vh-8rem)] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4">
              {/* Header */}
              <div className="flex items-center gap-3 px-4 py-3 bg-[#0F5156] text-white rounded-t-2xl">
                <div className="p-1.5 bg-white/20 rounded-lg">
                  <Bot className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold">Assistant RH</p>
                  <p className="text-xs text-white/70">Propulse par Claude</p>
                </div>
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                  <span className="text-xs text-white/70 mr-2">En ligne</span>
                  <button onClick={() => setChatOpen(false)} className="p-1 hover:bg-white/20 rounded-lg transition-colors">
                    <Minus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {chatMessages.length === 0 && !chatLoading && (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <div className="p-3 bg-[#0F5156]/10 rounded-full mb-3">
                      <Sparkles className="w-8 h-8 text-[#0F5156]" />
                    </div>
                    <p className="text-sm font-medium text-slate-900 dark:text-white">Bonjour {user?.prenom} !</p>
                    <p className="text-xs text-slate-500 mt-1 mb-4 max-w-[240px]">
                      Je suis votre assistant RH. Posez-moi vos questions sur les conges, la paie, les entretiens...
                    </p>
                    <div className="flex flex-wrap gap-1.5 justify-center">
                      {['Solde de congés ?', 'Notes de frais', 'Entretien annuel'].map((q, i) => (
                        <button key={i} onClick={() => setChatInput(q)} className="text-xs px-2.5 py-1.5 rounded-full border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {chatMessages.map((msg, idx) => (
                  <div key={idx} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {msg.role === 'assistant' && (
                      <div className="w-6 h-6 rounded-full bg-[#0F5156] flex items-center justify-center shrink-0 mt-0.5">
                        <Bot className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                    <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                      msg.role === 'user'
                        ? 'bg-[#0F5156] text-white rounded-br-md'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-white rounded-bl-md'
                    }`}>
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex gap-2">
                    <div className="w-6 h-6 rounded-full bg-[#0F5156] flex items-center justify-center shrink-0">
                      <Bot className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div className="bg-slate-100 dark:bg-slate-700 px-4 py-3 rounded-2xl rounded-bl-md">
                      <div className="flex gap-1">
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}} />
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={handleChatSubmit} className="p-3 border-t border-slate-200 dark:border-slate-700 flex gap-2">
                <input
                  ref={chatInputRef}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Posez votre question..."
                  disabled={chatLoading}
                  className="flex-1 text-sm px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0F5156]/30 focus:border-[#0F5156] disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={chatLoading || !chatInput.trim()}
                  className="p-2 bg-[#0F5156] text-white rounded-xl hover:bg-[#0d464a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            </div>
          )}

          {/* Chat FAB Button */}
          <button
            onClick={() => setChatOpen(prev => !prev)}
            className={`fixed bottom-20 right-4 md:bottom-6 md:right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
              chatOpen
                ? 'bg-slate-600 hover:bg-slate-700 scale-90'
                : 'bg-[#0F5156] hover:bg-[#0d464a] hover:scale-105'
            }`}
            style={chatOpen ? { bottom: 'auto', position: 'fixed', right: '1rem', top: '50%', display: 'none' } : {}}
          >
            {chatOpen ? (
              <X className="w-6 h-6 text-white" />
            ) : (
              <MessageSquare className="w-6 h-6 text-white" />
            )}
          </button>
          {!chatOpen && (
            <span className="fixed bottom-[5.5rem] right-4 md:bottom-[4.5rem] md:right-6 z-50 pointer-events-none">
              <span className="absolute -top-1 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white" />
            </span>
          )}
        </>
      )}

      {/* Mobile Bottom Navigation */}
      <nav className="mobile-nav" data-testid="mobile-bottom-nav">
        {navItems.slice(0, 5).map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`mobile-nav-item ${isActive ? 'active' : ''}`}
              data-testid={`mobile-nav-${item.path.replace('/', '') || 'dashboard'}`}
            >
              <Icon className="w-5 h-5" strokeWidth={isActive ? 2 : 1.5} />
              <span className="text-xs">{item.label.split(' ')[0]}</span>
            </Link>
          );
        })}
      </nav>

      {/* M5.3 Support widget — bottom-left to avoid overlapping the AI assistant. */}
      {user && <ChatWidget />}
    </div>
  );
};

export default Layout;
